// SPDX-License-Identifier: BUSL-1.1

/**
 * <AgentFleetActivity> — agent-filtered view of the org-wide fleet
 * activity SSE buffer.
 *
 * Used as the live-stream surface for any agent that does NOT emit to a
 * per-session SSE (pi-SDK runners, aider, custom — anything advertising
 * `capabilities.livePerSessionStream !== true`). Reads from the shared
 * <FleetActivityStreamProvider> context that powers <LiveActivityCell>
 * on the operators grid — no second SSE connection.
 *
 * Extracted from operator-activity-drilldown.tsx during the NocoDB
 * #4789 Layer B PR to keep that file under the gen-003 500-line FAIL
 * threshold.
 */

import { useContext } from 'react';
import {
  FleetActivityStreamContext,
  type FleetActivityStreamContextValue,
} from '@/components/live-activity-cell';
import { parseActivityPayload } from '@/components/agent-detail/activity-payload-schemas';

/** Same color scheme as LiveActivityCell + LiveStreamPanel + JobLiveActivitySection. */
const KIND_TONE: Record<string, string> = {
  tool_call: 'bg-purple-900/30 text-purple-300',
  artifact: 'bg-green-900/30 text-green-300',
  decision: 'bg-blue-900/30 text-blue-300',
  context_compressed: 'bg-amber-900/30 text-amber-300',
  blocker: 'bg-red-900/30 text-red-300',
  task_started: 'bg-cyan-900/30 text-cyan-300',
  task_completed: 'bg-emerald-900/30 text-emerald-300',
};

function NoLiveStream({ reason }: { reason: string }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-4 text-xs text-neutral-400">
      <p className="font-medium text-neutral-300">No live stream available</p>
      <p className="mt-1 text-neutral-500">{reason}</p>
    </div>
  );
}

export function AgentFleetActivity({ agentId }: { agentId: string }) {
  const ctx = useContext<FleetActivityStreamContextValue | null>(
    FleetActivityStreamContext,
  );

  if (!ctx) {
    return (
      <NoLiveStream reason="Fleet stream provider not mounted — open this drilldown from /operators." />
    );
  }
  if (ctx.error) {
    return (
      <NoLiveStream
        reason={`Stream offline: ${ctx.error}. Reconnecting automatically.`}
      />
    );
  }

  const myEvents = ctx.events
    .filter((e) => e.agentId === agentId)
    .slice(-50)
    .reverse(); // newest first

  if (myEvents.length === 0) {
    return (
      <NoLiveStream
        reason={
          ctx.connected
            ? `Subscribed — waiting for ${agentId} to emit activity. Events appear here as they happen.`
            : 'Connecting to fleet stream…'
        }
      />
    );
  }

  return (
    <div className="space-y-1 rounded-md border border-neutral-800 bg-neutral-900/30 p-2">
      {myEvents.map((event) => {
        const parsed = parseActivityPayload(event.kind, event.payload ?? {});
        let preview = '';
        switch (parsed.kind) {
          case 'tool_call':
            preview = parsed.data.tool;
            break;
          case 'artifact':
            preview = parsed.data.title ?? parsed.data.uri;
            break;
          case 'decision':
            preview = parsed.data.summary;
            break;
          case 'context_compressed':
            preview = `${parsed.data.before_tokens} → ${parsed.data.after_tokens} tok`;
            break;
          case 'blocker':
            preview =
              parsed.data.kind === 'stall_suspected'
                ? `🤖 ${parsed.data.detail}`
                : parsed.data.detail;
            break;
          case 'task_started':
            preview = parsed.data.title ?? parsed.data.task_id;
            break;
          case 'task_completed':
            preview = parsed.data.outcome ?? parsed.data.task_id;
            break;
          case 'unknown':
            preview = JSON.stringify(parsed.raw).slice(0, 80);
            break;
        }
        return (
          <div
            key={event.id}
            className="flex items-center gap-2 truncate text-xs"
            title={`${event.kind} — ${event.occurredAt}`}
          >
            <span
              className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${KIND_TONE[event.kind] ?? 'bg-neutral-700 text-neutral-300'}`}
            >
              {event.kind}
            </span>
            <span className="truncate font-mono text-neutral-400">
              {preview}
            </span>
            <span className="ml-auto shrink-0 text-[10px] text-neutral-600">
              {new Date(event.occurredAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
